import { useCallback, useMemo } from "react";
import {
  DataTable,
  DateRangeSelector,
  Divider,
  Grid,
  Tabs,
  Tag,
  Text,
  useClientTable,
} from "@pushpress/shared-components";
import {
  CelebrationItem,
  CelebrationType,
  celebrationTypes,
} from "./Celebrations.utils";
import UserAvatar from "../../../components/common/UserAvatar/UserAvatar";
import { joinStrings } from "../../../utils/misc";
import { DateRangeValue } from "@pushpress/shared-components/build/components/selectors/DateRangeSelector/DateRangeSelector.types";
import { formatInTimeZone } from "date-fns-tz";

interface CelebrationsTableProps {
  dateRange: DateRangeValue;
  data: CelebrationItem[];
  celebrationType: CelebrationType;
  allCount: number;
  birthdayCount: number;
  anniversaryCount: number;
  loading: boolean;
  onCelebrationTypeChange: (celebrationType: CelebrationType) => void;
  onDateRangeChange: (dateRange: DateRangeValue) => void;
}

function CelebrationsTable(props: CelebrationsTableProps) {
  const {
    data: dataProp,
    celebrationType,
    dateRange,
    allCount,
    birthdayCount,
    anniversaryCount,
    loading,
    onCelebrationTypeChange,
    onDateRangeChange,
  } = props;

  const columns = useMemo(
    () => [
      {
        key: "firstName",
        header: "Name",
        isSortable: true,
        sort: (a: CelebrationItem, b: CelebrationItem) =>
          joinStrings(a.firstName, a.lastName).localeCompare(
            joinStrings(b.firstName, b.lastName),
          ),
        cell: (item: CelebrationItem) => {
          return (
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <UserAvatar data={item} size="medium" />
              </Grid>
              <Grid item>
                <Text
                  variant="body-lg"
                  component="a"
                  color="pacific-primary-600"
                  href={`/members/view/${item.uuid}`}
                >
                  {joinStrings(item.firstName, item.lastName)}
                </Text>
              </Grid>
            </Grid>
          );
        },
      },
      {
        key: "type",
        header: "Type",
        cell: (item: CelebrationItem) => {
          return <Tag size="medium">{item.type}</Tag>;
        },
      },
      {
        key: "date",
        header: "Date",
        isSortable: true,
        sort: (a: CelebrationItem, b: CelebrationItem) =>
          a.date.getTime() - b.date.getTime(),
        cell: (item: CelebrationItem) => {
          return (
            <Text variant="body-lg">
              {formatInTimeZone(item.date, "UTC", "EEE, MMM dd")}
            </Text>
          );
        },
      },
    ],
    [],
  );

  const { data, sort, handleSort } = useClientTable({
    data: dataProp,
    columns,
    defaultSort: { key: "date", order: "asc" },
  });

  const getRowId = useCallback((row) => {
    return `${row.celebrationType}-${row.uuid}`;
  }, []);

  const handleTabClick = (index: number) => {
    onCelebrationTypeChange(celebrationTypes[index]);
  };

  const tabHeaders = [
    `All (${allCount})`,
    `Birthdays (${birthdayCount})`,
    `Anniversaries (${anniversaryCount})`,
  ];

  const tabIndex = celebrationTypes.findIndex(
    (type) => type === celebrationType,
  );

  return (
    <DataTable
      customHeader={
        <Grid container className="p-3" spacing={4} alignItems="center">
          <Grid item>
            <DateRangeSelector value={dateRange} onChange={onDateRangeChange} />
          </Grid>
          <Grid item>
            <Divider vertical />
          </Grid>
          <Grid item style={{ width: 480 }}>
            <Tabs
              variant="buttons"
              tabHeaders={tabHeaders}
              selectedIndex={tabIndex}
              onTabClick={handleTabClick}
            />
          </Grid>
        </Grid>
      }
      data={data}
      columns={columns}
      sort={sort}
      loading={loading}
      getRowId={getRowId}
      onSort={handleSort}
    />
  );
}

export default CelebrationsTable;
